/**
 * Turning what a payload carries into an instant, for everything here that reads one.
 *
 * Shared rather than duplicated because strictness only works if it is total: a second reader
 * parsing dates its own way would accept the shapes this one refuses, and the guarantee that a
 * rendered day is the day the endpoint sent would hold on one surface and not the next.
 *
 * Below the React layer rather than beside the components, because the calendar reads payloads too
 * and nothing in `format/` may reach upwards for it.
 */

/**
 * ISO 8601, with the offset optional and everything else exact.
 *
 * A shape outside this one is refused rather than guessed at. `new Date('10-22-2025')` succeeds on
 * a heuristic no specification describes: it picks a field order the payload never stated, and
 * resolves it against the host's zone. `10-11-2025` then renders a confidently wrong day.
 */
const ISO_INSTANT =
  /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?)(Z|[+-]\d{2}:?\d{2})?)?$/;

/** `+0200` and `+02:00` are the same offset, and only the second is one `Date` will read. */
const withOffsetColon = (offset: string): string =>
  offset.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2');

/**
 * A clock with its fraction held to the three digits the language actually specifies.
 *
 * ISO 8601 allows a fraction of any length, and backends use that: `Instant.toString()` emits
 * nanoseconds, .NET ticks seven digits, Postgres microseconds six. But the format `Date.parse` is
 * required to understand names *exactly* three, and for anything else the spec lets an engine fall
 * back to its own heuristics — so `.123456789` parsing at all is V8 being generous rather than the
 * language agreeing. Truncating here is what stops that from mattering: every string handed to
 * `Date` is one the spec describes, so the same payload resolves to the same instant on every
 * engine instead of on the ones tested.
 *
 * Truncated and not rounded, because a sub-millisecond remainder cannot be represented and
 * rounding it up would step over a boundary — `23:59:59.9999` becoming the next day.
 */
export const toMillisecondClock = (time: string): string => {
  const [clock, fraction] = time.split('.');

  return fraction === undefined
    ? clock
    : `${clock}.${fraction.slice(0, 3).padEnd(3, '0')}`;
};

/** What a payload turned out to be carrying. */
export type Parsed = {
  readonly date: Date;
  /**
   * The day, when the payload named one and no time at all: `2026-01-15`. A day is not an instant
   * and belongs to no zone, so it is rendered rather than converted.
   */
  readonly day?: string;
};

/**
 * An ISO string as an instant, reading a missing offset as UTC.
 *
 * A time of day carrying no offset is UTC here, where `Date` would call it host-local. The zone a
 * reader is in comes from the backend, so two laptops in different places have to resolve one
 * payload to one instant — otherwise the same row reads six hours apart depending on where it is
 * opened, while looking entirely plausible in both.
 */
const fromIsoString = (value: string): Parsed | undefined => {
  const parts = ISO_INSTANT.exec(value.trim());

  if (!parts) return undefined;

  const [, date, time, offset] = parts;

  return time === undefined
    ? { date: new Date(`${date}T00:00:00Z`), day: date }
    : {
        date: new Date(
          `${date}T${toMillisecondClock(time)}${
            offset ? withOffsetColon(offset) : 'Z'
          }`
        ),
      };
};

/**
 * Coerces what a payload might carry into a real instant.
 *
 * `Intl.DateTimeFormat().format(new Date('nope'))` throws `RangeError: Invalid time value`, so an
 * unparseable value would take down whatever page it appears on. It renders as empty instead.
 */
export const parseValue = (
  value: Date | string | number | null | undefined
): Parsed | undefined => {
  if (value === null || value === undefined) return undefined;

  const parsed =
    value instanceof Date
      ? { date: value }
      : typeof value === 'string'
        ? fromIsoString(value)
        : { date: new Date(value) };

  return parsed === undefined || Number.isNaN(parsed.date.getTime())
    ? undefined
    : parsed;
};
